// MobileShell — esqueleto navegável da superfície MOBILE logada. Paredes A3
// (base) e A5 (abas de catálogo + tela de detalhe).
//
// Estrutura (docs/specs/mobile-app-ux.md §4):
//   ┌─ TopBar (logo + busca + perfil) ────────────┐
//   │  conteúdo scrollável da aba ativa            │
//   └─ Bottom tab bar (Início/Ao vivo/Filmes/…) ──┘
//
// A aba ativa é estado local (useState) — troca por TAP, sem react-router e
// sem animação cara. A TV usa TopNav focável + rotas; aqui é tab bar inferior
// (alcance do polegar) sem foco de remoto. Safe-area inferior respeitada.
//
// A lógica de "qual view mostrar" vive em mobile-shell-view.ts (pura, testável
// sem DOM). A5 preenche as 4 abas de catálogo (Ao vivo/Filmes/Séries/Minha
// lista) e a tela de DETALHE (VOD/série). Fluxo touch: tap num card →
// DETALHE → Assistir → PLAYER (A4). Canal vai direto pro player (sem detalhe).
// A6 preenche a BUSCA (MobileSearch, reusa api.search) e o CONFIGURAR FONTE
// (MobileConfigureSource, reusa PUT /swipe-account/preference). A7 preenche o
// PERFIL/AJUSTES (MobileProfile): conta+logout, perfis, idioma, fonte, versão —
// último placeholder do app. O `onLogout` sobe do MobileRoot (encerra a sessão
// Swipe e volta pro login).

import { useTranslation } from '@app-iptv/i18n'
import { useState } from 'react'

import { accountKey as accountKeyOf, useSessionStore } from '../store'

import {
  activeTab,
  goBackState,
  INITIAL_SHELL_STATE,
  resolveMobileView,
  type MobileDetailTarget,
  type MobilePlayerTarget,
  type MobileShellState,
  type MobileTab,
} from './mobile-shell-view'
import { bootstrapMobileSync, useMobileSyncUi } from './mobile-sync-bootstrap'
import { MobileConfigureSource } from './MobileConfigureSource'
import { MobileDetail } from './MobileDetail'
import { MobileHome, type MobileHomeItem } from './MobileHome'
import { MobileLive } from './MobileLive'
import { MobileMovies } from './MobileMovies'
import { MobileMyList } from './MobileMyList'
import { MobilePlayer } from './MobilePlayer'
import { MobileProfile } from './MobileProfile'
import {
  MobileAccountLoadFailed,
  MobileActiveProfileChip,
  MobileNoActiveProfile,
  MobileProfilePicker,
} from './MobileProfilePicker'
import { MobileSearch } from './MobileSearch'
import { MobileSeries } from './MobileSeries'
import styles from './MobileShell.module.css'

const TABS: ReadonlyArray<{ id: MobileTab; icon: string; labelKey: string }> = [
  { id: 'home', icon: '⌂', labelKey: 'nav.home' },
  { id: 'live', icon: '▣', labelKey: 'nav.live' },
  { id: 'movies', icon: '▷', labelKey: 'nav.movies' },
  { id: 'series', icon: '☰', labelKey: 'nav.series' },
  { id: 'mylist', icon: '♥', labelKey: 'mobileShell.myList' },
]

export interface MobileShellProps {
  /** Encerra a sessão Swipe e volta pro login. Sobe do MobileRoot (que faz
   *  endSwipeSync + session.logout → MobileAuthFlow, sem estado órfão). */
  onLogout: () => void
}

export function MobileShell({ onLogout }: MobileShellProps): JSX.Element {
  const { t } = useTranslation()
  const [state, setState] = useState<MobileShellState>(INITIAL_SHELL_STATE)

  // ===== Perfil ativo (decisão do dono, 10/10/2026) =====
  // A folha de escolha abre: sozinha, UMA vez, quando o bootstrap não pôde decidir
  // (`prompt: 'ask'`); pelo chip do cabeçalho ("Trocar perfil"); pelo aviso de "sem
  // perfil ativo". Ver MobileProfilePicker.tsx.
  const account = useSessionStore((s) => accountKeyOf(s))
  const activeProfileId = useSessionStore((s) => s.activeProfileId)
  const prompt = useMobileSyncUi((s) => s.prompt)
  const accountLoadFailed = useMobileSyncUi((s) => s.accountLoadFailed)
  const [pickerMode, setPickerMode] = useState<'choose' | 'switch' | null>(null)
  const openPicker: 'choose' | 'switch' | null = pickerMode ?? (prompt === 'ask' ? 'choose' : null)
  const closePicker = (): void => {
    setPickerMode(null)
    useMobileSyncUi.setState({ prompt: 'none' })
  }

  // Tap em card/hero. O padrão touch é DETALHE → Assistir: VOD e série abrem a
  // tela de detalhe (sinopse/episódios/favoritar) antes do player. Canal não
  // tem detalhe (é zapping ao vivo), vai direto pro player.
  const handleOpenItem = (item: MobileHomeItem): void => {
    if (item.kind === 'channel') {
      setState((s) => ({
        ...s,
        overlay: 'player',
        detail: null,
        player: {
          id: item.id,
          kind: 'channel',
          title: item.title,
          poster: item.poster,
          ...(item.resumeFrom !== undefined ? { resumeFrom: item.resumeFrom } : {}),
        },
      }))
      return
    }
    // VOD/série → detalhe.
    const detail: MobileDetailTarget = {
      id: item.id,
      kind: item.kind,
      title: item.title,
      poster: item.poster,
    }
    // Marca a origem quando o detalhe é aberto a partir da BUSCA, pra o
    // voltar retornar pra busca (com estado restaurado) e não pra Home —
    // fix/busca-estado-perdido. Abertura por uma aba não precisa de marca.
    setState((s) => ({
      ...s,
      overlay: 'detail',
      detail,
      player: null,
      detailFrom: s.overlay === 'search' ? 'search' : null,
    }))
  }

  // Assistir a partir do detalhe → abre o player POR CIMA do detalhe (o detalhe
  // fica no estado pra o ← do player voltar pra ele, não pra aba).
  const playFromDetail = (target: MobilePlayerTarget): void =>
    setState((s) => ({ ...s, overlay: 'player', player: target }))

  // Tap no CTA "configurar fonte" (estado no_provider) → fluxo real (A6).
  const openConfigureSource = (): void =>
    setState((s) => ({ ...s, overlay: 'configureSource' }))

  // Fonte cadastrada+validada: fecha o overlay e volta pra Home (que agora
  // carrega o catálogo — saiu do no_provider). As queries já foram invalidadas
  // dentro do MobileConfigureSource.
  const handleConfigured = (): void =>
    setState({ tab: 'home', overlay: null, player: null, detail: null })

  const openSearch = (): void => setState((s) => ({ ...s, overlay: 'search' }))
  const openProfile = (): void => setState((s) => ({ ...s, overlay: 'profile' }))

  const selectTab = (tab: MobileTab): void =>
    setState({ tab, overlay: null, player: null, detail: null })

  // Voltar: hierarquia em goBackState (pura, testável). Player sobre detalhe →
  // detalhe; detalhe vindo da BUSCA → volta pra busca (estado restaurado),
  // não pra Home; senão fecha tudo e volta pra aba.
  const goBack = (): void => setState(goBackState)

  const view = resolveMobileView(state)
  const active = activeTab(state)
  const showBack = view.kind !== 'tab'

  // O player (A4) é uma TELA CHEIA: assume o shell inteiro (sem topbar/tab bar
  // — ele tem seus próprios controles touch e botão de voltar). Renderiza antes
  // do shell normal pra evitar montar a topbar/tab bar por baixo.
  if (view.kind === 'player') {
    return (
      <MobilePlayer
        id={view.target.id}
        kind={view.target.kind}
        title={view.target.title}
        poster={view.target.poster}
        resumeFrom={view.target.resumeFrom}
        seriesId={view.target.seriesId}
        episodeId={view.target.episodeId}
        seasonNumber={view.target.seasonNumber}
        episodeNumber={view.target.episodeNumber}
        onClose={goBack}
      />
    )
  }

  let content: JSX.Element
  if (view.kind === 'detail') {
    content = (
      <MobileDetail
        id={view.target.id}
        kind={view.target.kind}
        title={view.target.title}
        poster={view.target.poster}
        onPlay={playFromDetail}
        onConfigureSource={openConfigureSource}
      />
    )
  } else if (view.kind === 'search') {
    content = (
      <MobileSearch onOpenItem={handleOpenItem} onConfigureSource={openConfigureSource} />
    )
  } else if (view.kind === 'configureSource') {
    content = <MobileConfigureSource onConfigured={handleConfigured} />
  } else if (view.kind === 'profile') {
    content = <MobileProfile onLogout={onLogout} onConfigureSource={openConfigureSource} />
  } else if (view.tab === 'home') {
    content = (
      <MobileHome
        onOpenItem={handleOpenItem}
        onConfigureSource={openConfigureSource}
        onOpenProfile={openProfile}
      />
    )
  } else if (view.tab === 'live') {
    content = <MobileLive onOpenItem={handleOpenItem} onConfigureSource={openConfigureSource} />
  } else if (view.tab === 'movies') {
    content = <MobileMovies onOpenItem={handleOpenItem} onConfigureSource={openConfigureSource} />
  } else if (view.tab === 'series') {
    content = <MobileSeries onOpenItem={handleOpenItem} onConfigureSource={openConfigureSource} />
  } else {
    content = <MobileMyList onOpenItem={handleOpenItem} />
  }

  // Conta com fonte e SEM perfil ativo, numa aba de catálogo: o aviso que conduz à
  // escolha. Sem perfil nada se grava (scope.ts), então o aviso diz o porquê.
  const noActive = account !== null && !activeProfileId && view.kind === 'tab'

  return (
    <div className={styles.shell}>
      {/* ===== TopBar ===== */}
      <header className={styles.topbar}>
        {showBack ? (
          <button
            type="button"
            className={styles.topIcon}
            aria-label={t('mobileShell.back')}
            onClick={goBack}
          >
            ‹
          </button>
        ) : (
          <span className={styles.brand}>{t('mobileAuth.brand')}</span>
        )}
        <div className={styles.topActions}>
          <MobileActiveProfileChip onPress={() => setPickerMode('switch')} />
          <button
            type="button"
            className={styles.topIcon}
            aria-label={t('nav.search')}
            data-action="search"
            onClick={openSearch}
          >
            ⌕
          </button>
          <button
            type="button"
            className={styles.topIcon}
            aria-label={t('mobileShell.profile')}
            data-action="profile"
            onClick={openProfile}
          >
            ☰
          </button>
        </div>
      </header>

      {/* ===== Conteúdo scrollável da aba ativa ===== */}
      <main className={styles.content}>
        {/* A fonte da conta não veio e o store ficou sem contexto: o erro SUBSTITUI a aba
            (uma Home vazia por baixo repetiria "Nada por aqui ainda" sem razão). */}
        {accountLoadFailed && account === null && view.kind === 'tab' ? (
          <MobileAccountLoadFailed onRetry={() => void bootstrapMobileSync()} />
        ) : (
          <>
            {noActive && <MobileNoActiveProfile onChoose={() => setPickerMode('choose')} />}
            {content}
          </>
        )}
      </main>

      {openPicker && (
        <MobileProfilePicker
          mode={openPicker}
          onClose={closePicker}
          onOpenProfile={() => {
            closePicker()
            openProfile()
          }}
        />
      )}

      {/* ===== Bottom tab bar ===== */}
      <nav className={styles.tabbar} aria-label={t('mobileShell.tabsLabel')}>
        {TABS.map((tabDef) => {
          const isActive = active === tabDef.id
          return (
            <button
              key={tabDef.id}
              type="button"
              className={styles.tab}
              data-tab={tabDef.id}
              data-active={isActive ? 'true' : 'false'}
              aria-current={isActive ? 'page' : undefined}
              onClick={() => selectTab(tabDef.id)}
            >
              <span className={styles.tabIcon} aria-hidden="true">
                {tabDef.icon}
              </span>
              <span className={styles.tabLabel}>{t(tabDef.labelKey)}</span>
            </button>
          )
        })}
      </nav>
    </div>
  )
}
